import { useCallback, useEffect, useState } from "react";

const KEY = "sw_recently_viewed_v1";
const MAX = 6;
const EVENT = "recently-viewed:change";

// Minimal product fields we cache to keep storage tiny.
const PICK = ["id", "name", "category", "price", "mrp", "image_url", "rating", "sold", "seller_name", "seller_city", "seller_id", "stock", "image_urls"];

function pick(p) {
  if (!p || !p.id) return null;
  const out = {};
  for (const k of PICK) if (k in p) out[k] = p[k];
  // image_urls can be heavy; keep at most first 3
  if (Array.isArray(out.image_urls)) out.image_urls = out.image_urls.slice(0, 3);
  return out;
}

function read() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function write(list) {
  try { sessionStorage.setItem(KEY, JSON.stringify(list)); } catch { /* ignore quota */ }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: list }));
}

/**
 * Session-scoped "recently viewed" list — last 6 products the user opened.
 * No backend. Resets when the browser/tab session ends. Cross-tab sync via
 * sessionStorage is intentionally NOT used (each tab has its own session).
 */
export function useRecentlyViewed() {
  const [recent, setRecent] = useState(() => read());

  useEffect(() => {
    const onChange = () => setRecent(read());
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, []);

  const track = useCallback((product) => {
    const slim = pick(product);
    if (!slim) return;
    const current = read();
    const dedup = [slim, ...current.filter((x) => x.id !== slim.id)].slice(0, MAX);
    write(dedup);
    setRecent(dedup);
  }, []);

  const clear = useCallback(() => {
    write([]);
    setRecent([]);
  }, []);

  return { recent, track, clear };
}
